import { Box } from "@material-ui/core";
import React, { useEffect, useMemo } from "react";
import {
  CarReservation,
  CarReservationState,
  ExperienceReservationState,
  FlightItineraryState,
  HomesReservationState,
  HotelItineraryState,
  PackageItineraryState,
  HomesItinerary,
  PackageItinerary,
  ExperienceReservation,
  HotelItinerary,
  BookedFlightItineraryWithDepartureTime,
  isMultiCityItinerary,
} from "redmond";
import { useQueryParams } from "@capone/common";
import { RouteComponentProps } from "react-router";
import dayjs from "dayjs";
import { CancelGroundModalContent } from "../ItinerariesModal/components/CancelGroundModalContent";
import { TravelAppPageConnectorProps } from "./container";
import { CancelHomeModalContent } from "../ItinerariesModal/components/CancelHomeModalContent";
import { ChangeGroundModalContent } from "../ItinerariesModal/components/ChangeGroundModalContent";
import { CancelPackageHotelModalContent } from "../ItinerariesModal/components/CancelPackageHotelModalContent";
import { ChangePackageHotelModalContent } from "../ItinerariesModal/components/ChangePackageHotelModalContent";
import { SelfServeCancelFlightModalContent } from "../ItinerariesModal/components/SelfServeCancelFlightModalContent";
import { CancelExperienceModalContent } from "../ItinerariesModal/components/CancelExperienceModalContent";
import { CancelHotelModalContent } from "../ItinerariesModal/components/CancelHotelModalContent";
import { ChangeHotelModalContentV2 } from "../ItinerariesModal/components/ChangeHotelModalContentV2";
import { ExchangePackageFlightModalContent } from "../ItinerariesModal/components/ExchangePackageFlightModalContent";
import { ExchangeFlightModalContent } from "../ItinerariesModal/components/ExchangeFlightModalContent";
import { CfarFlightModalContent } from "../ItinerariesModal/components/CfarFlightModalContent";
import { CfarHotelModalContent } from "../ItinerariesModal/components/CfarHotelModalContent";

export interface ITravelAppPageProps
  extends TravelAppPageConnectorProps,
    RouteComponentProps {}

export const TravelAppPage = ({
  selectedFlight,
  selectedHotel,
  selectedCar,
  selectedHome,
  selectedExperience,
  history,
  fetchFlights,
  fetchHotels,
  fetchCars,
  fetchPackages,
  fetchExperiences,
  fetchHomes,
  airportMap,
  selectedPackage,
}: ITravelAppPageProps) => {
  const params = useQueryParams();

  const { action, itineraryType } = params;

  const selectedItinerary = useMemo(() => {
    switch (itineraryType) {
      case "flight":
        return selectedFlight;
      case "hotel":
        return selectedHotel;
      case "car":
        return selectedCar;
      case "home":
        return selectedHome;
      case "package":
      case "package-hotel":
      case "package-flight":
        return selectedPackage;

      case "activity":
        return selectedExperience;
      default:
        return undefined;
    }
  }, [
    selectedFlight,
    selectedHotel,
    selectedCar,
    selectedHome,
    selectedPackage,
    selectedExperience,
    itineraryType,
  ]);

  useEffect(() => {
    switch (itineraryType) {
      case "flight":
        fetchFlights(
          {
            states: [FlightItineraryState.Present, FlightItineraryState.Future],
            referenceDateTime: dayjs().format(),
          },
          history,
          true,
        );
        fetchFlights(
          {
            states: [FlightItineraryState.Canceled, FlightItineraryState.Past],
            referenceDateTime: dayjs().format(),
          },
          history,
        );
        break;

      case "hotel":
        fetchHotels(
          {
            states: [HotelItineraryState.Present, HotelItineraryState.Future],
            referenceDateTime: dayjs().format(),
          },
          history,
        );

        fetchHotels(
          {
            states: [HotelItineraryState.Canceled, HotelItineraryState.Past],
            referenceDateTime: dayjs().format(),
          },
          history,

          [HotelItineraryState.Canceled],
        );
        break;
      case "car":
        fetchCars(
          {
            states: [CarReservationState.Present, CarReservationState.Future],
            asOf: new Date().toISOString(),
          },
          history,
        );
        fetchCars(
          {
            states: [CarReservationState.Canceled, CarReservationState.Past],
            asOf: new Date().toISOString(),
          },
          history,
        );
        break;
      case "home":
        fetchHomes(
          {
            states: [
              HomesReservationState.Present,
              HomesReservationState.Future,
              HomesReservationState.Canceled,
              HomesReservationState.Past,
            ],
            referenceDateTime: dayjs().format(),
          },
          history,
        );
        break;

      case "package-hotel":
      case "package-flight":
        fetchPackages(
          {
            states: [
              PackageItineraryState.Present,
              PackageItineraryState.Future,
              PackageItineraryState.Canceled,
              PackageItineraryState.Past,
            ],
            referenceDateTime: dayjs().format(),
          },
          history,
        );
        break;
      case "activity":
        fetchExperiences(
          {
            states: [
              ExperienceReservationState.Present,
              ExperienceReservationState.Future,
              ExperienceReservationState.Canceled,
              ExperienceReservationState.Past,
            ],
            referenceDateTime: dayjs().format(),
          },
          history,
        );
        break;
      default:
    }
  }, [itineraryType, action]);

  const renderPageContent = () => {
    switch (itineraryType) {
      case "flight":
        if (action === "cancel") {
          return (
            <SelfServeCancelFlightModalContent
              flight={
                selectedItinerary as BookedFlightItineraryWithDepartureTime
              }
            />
          );
        }
        if (action === "change") {
          const isMultiCity = isMultiCityItinerary(
            (selectedItinerary as BookedFlightItineraryWithDepartureTime)
              .bookedItinerary,
            airportMap,
          );
          return (
            <ExchangeFlightModalContent
              flight={
                selectedItinerary as BookedFlightItineraryWithDepartureTime
              }
              isMultiCity={isMultiCity}
            />
          );
        }
        if (action === "cfar") {
          return (
            <CfarFlightModalContent
              onClose={() => {}}
              setHideXButton={() => {}}
              setDisableOnClose={() => {}}
              setUsePartialScroll={() => {}}
              itineraryId={
                (selectedItinerary as BookedFlightItineraryWithDepartureTime)
                  .bookedItinerary.id
              }
              flight={
                selectedItinerary as BookedFlightItineraryWithDepartureTime
              }
            />
          );
        }
        return null;
      case "hotel":
        if (action === "cancel") {
          return (
            <CancelHotelModalContent
              hotel={selectedItinerary as HotelItinerary}
              isMobile
            />
          );
        }
        if (action === "change") {
          return (
            <ChangeHotelModalContentV2
              hotel={selectedItinerary as HotelItinerary}
            />
          );
        }
        if (action === "cfar") {
          return (
            <CfarHotelModalContent
              onClose={() => {}}
              setHideXButton={() => {}}
              setDisableOnClose={() => {}}
              setUsePartialScroll={() => {}}
              hotel={selectedItinerary as HotelItinerary}
            />
          );
        }
        return null;
      case "car":
        if (action === "cancel") {
          return (
            <CancelGroundModalContent
              groundBooking={selectedItinerary as CarReservation}
            />
          );
        }
        if (action === "change") {
          return (
            <ChangeGroundModalContent
              groundBooking={selectedItinerary as CarReservation}
            />
          );
        }
        return null;
      case "home":
        if (action === "cancel") {
          return (
            <CancelHomeModalContent
              home={selectedItinerary as HomesItinerary}
            />
          );
        }
        return null;

      case "package-hotel":
        if (action === "cancel") {
          return (
            <CancelPackageHotelModalContent
              packageItinerary={selectedItinerary as PackageItinerary}
              isMobile
            />
          );
        }
        if (action === "change") {
          return (
            <ChangePackageHotelModalContent
              packageItinerary={selectedItinerary as PackageItinerary}
              isMobile
            />
          );
        }
        return null;
      case "package-flight":
        if (action === "cancel") {
          return (
            <SelfServeCancelFlightModalContent
              flight={(selectedItinerary as PackageItinerary).flight.itinerary}
              isPackageFlight
            />
          );
        }
        if (action === "change") {
          return (
            <ExchangePackageFlightModalContent
              packageItinerary={selectedItinerary as PackageItinerary}
            />
          );
        }
        return null;
      case "activity":
        if (action === "cancel") {
          return (
            <CancelExperienceModalContent
              experience={selectedItinerary as ExperienceReservation}
            />
          );
        }
        return null;
      default:
        return null;
    }
  };
  return <Box>{selectedItinerary && renderPageContent()}</Box>;
};
